/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

function getThemeAssetUrl(filename) {
  const assetHref = document.querySelector('link[href*="/assets/crs-design-system.css"]')?.getAttribute('href') || '';
  const assetBase = assetHref.split('/assets/')[0];
  return `${assetBase}/assets/${filename}`;
}

function getCynusFeatureHighlights() {
  return [
    {
      image: 'crs-pdp-fold-go-feature-16x9.webp',
      alt: 'Cynus foldable case-style chess board opened for real-board AI chess play',
      kicker: 'Fold & Go',
      title: 'Open the board, play, and store it cleanly.',
      text: 'The foldable case-style design keeps the robot board practical for home, study, and portable play settings.',
      span: 'standard',
      fit: 'contain',
    },
    {
      image: 'crs-pdp-ai-board-recognition-feature-16x9.webp',
      alt: 'Cynus camera head recognizing a physical chessboard position on the real board',
      kicker: 'AI board recognition',
      title: 'Cynus follows the real board.',
      text: 'Board recognition keeps the game connected to physical pieces instead of making every move live only on a screen.',
      span: 'hero',
      fit: 'contain',
    },
    {
      image: 'crs-pdp-robotic-movement-feature-16x9.webp',
      alt: 'Cynus robotic arm moving a chess piece on the physical board',
      kicker: 'Robotic movement',
      title: 'The AI move happens on the table.',
      text: 'AI responses become physical moves you can watch on the board, not just coordinates on a screen.',
      span: 'standard',
      fit: 'cover',
    },
    {
      image: 'crs-pdp-app-connected-play-feature-16x9.webp',
      alt: 'Cynus app-connected play visual showing the official app alongside the physical robot chess board',
      kicker: 'App-connected play',
      title: 'Connect, choose a mode, and start playing.',
      text: 'Use the official app for connection, match setup, and firmware/update resources.',
      span: 'standard',
      fit: 'contain',
    },
  ];
}

function getCynusProductStoryHighlights() {
  return [
    {
      image: 'crs-pdp-fold-go-feature-16x9.webp',
      alt: 'Cynus foldable case-style chess board opened for real-board AI chess play',
      kicker: 'Fold & Go',
      title: 'Open the board, play, and store it cleanly.',
      text: 'The foldable case-style design keeps the robot board practical for home, study, and portable play settings.',
      chips: ['Case-style board', 'Organized storage', 'Portable setup'],
      span: 'hero',
      fit: 'contain',
    },
    {
      video: 'crs-pdp-ai-board-recognition-card-16x9.mp4',
      poster: 'crs-pdp-ai-board-recognition-card-poster.webp',
      alt: 'Cynus camera recognition visual following the physical chessboard',
      kicker: 'AI board recognition',
      title: 'Cynus follows the real board.',
      text: 'Board recognition keeps the game connected to physical pieces instead of making every move live only on a screen.',
      chips: ['Camera view', 'Real board', 'Physical positions'],
      span: 'standard',
      fit: 'contain',
    },
    {
      video: 'crs-pdp-robotic-movement-card-16x9.mp4',
      poster: 'crs-pdp-robotic-movement-card-poster.webp',
      alt: 'Cynus robotic arm moving a real chess piece on the board',
      kicker: 'Robotic movement',
      title: 'The AI move happens on the table.',
      text: 'AI responses become physical moves you can watch on the board, not just coordinates on a screen.',
      chips: ['Robotic arm', 'Real pieces', 'Visible response'],
      span: 'wide',
      fit: 'cover',
    },
    {
      image: 'crs-pdp-app-connected-play-card.webp',
      alt: 'Cynus AI Match app screenshot composite with the physical robot chess board',
      kicker: 'App-connected play',
      title: 'Connect, choose a mode, and start playing.',
      text: 'Use the official app for connection, match setup, and firmware/update resources.',
      chips: ['Connect', 'Play', 'Modes'],
      span: 'wide',
      fit: 'contain',
    },
  ];
}

function initCynusHomeFeatureGrid() {
  if (window.location.pathname !== '/') return;

  const section = document.querySelector('.crs-feature-grid-section .crs-section[data-section-id*="crs_benefits"]');
  if (!section) return;

  const images = section.querySelectorAll('.crs-feature-card__media');
  getCynusFeatureHighlights().forEach((feature, index) => {
    const image = images[index];
    if (!image) return;

    image.setAttribute('src', getThemeAssetUrl(feature.image));
    image.setAttribute('alt', feature.alt);
  });
}

function initCynusFeatureStory() {
  if (!window.location.pathname.includes('/products/manya-cynus-portable-ai-chess-robot')) return;

  const section = document.querySelector('.crs-media-story[data-section-id*="crs_product_story_final"]');
  if (!section) return;

  const features = getCynusProductStoryHighlights();

  const header = section.querySelector('.crs-section__header');
  if (header) {
    header.innerHTML = [
      '<span class="crs-badge">Product experience</span>',
      '<h2 class="crs-section__title">Why Cynus feels different from a chess app.</h2>',
      '<p class="crs-section__intro">Cynus brings AI chess back to the table with a real board, physical pieces, robotic movement, and app-connected play.</p>',
    ].join('');
  }

  const grid = section.querySelector('.crs-media-story__grid');
  if (grid) {
    grid.innerHTML = features.map((feature) => {
      const media = feature.video ? `
        <video
          class="crs-media-story__image crs-media-story__video"
          poster="${getThemeAssetUrl(feature.poster)}"
          aria-label="${feature.alt}"
          autoplay
          loop
          muted
          playsinline
          preload="metadata"
        >
          <source src="${getThemeAssetUrl(feature.video)}" type="video/mp4">
        </video>
      ` : `
        <img
          class="crs-media-story__image"
          src="${getThemeAssetUrl(feature.image)}"
          alt="${feature.alt}"
          width="900"
          height="600"
          loading="lazy"
        >
      `;

      return `
      <article class="crs-media-story__card crs-media-story__card--${feature.span} crs-media-story__card--fit-${feature.fit} crs-media-story__card--layout-default">
        ${media}
        <div class="crs-media-story__body">
          <span class="crs-media-story__kicker">${feature.kicker}</span>
          <h3>${feature.title}</h3>
          <p>${feature.text}</p>
          <div class="crs-media-story__chips" aria-label="${feature.title} capabilities">
            ${feature.chips.map((chip) => `<span class="crs-media-story__chip">${chip}</span>`).join('')}
          </div>
        </div>
      </article>
    `;
    }).join('');
  }
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCynusHomeFeatureGrid, { once: true });
  document.addEventListener('DOMContentLoaded', initCynusFeatureStory, { once: true });
} else {
  initCynusHomeFeatureGrid();
  initCynusFeatureStory();
}
